import React, { useState, useEffect, useCallback } from "react";
import {
  Coins,
  Sparkles,
  Sliders,
  Search,
  RefreshCw,
  PlusCircle,
  MinusCircle,
  Clock,
  History,
  CheckCircle2,
  AlertCircle,
  X,
  ChevronLeft,
  ChevronRight,
  ShieldCheck,
  TrendingUp,
  ArrowUpRight,
  ArrowDownLeft,
  Edit2,
  Save,
} from "lucide-react";
import * as api from "@/src/api/sdk.gen";
import type {
  AdminOrgCreditOverview,
  PlatformPricingRuleResponse,
  CreditTransactionRow,
} from "@/src/api/types.gen";

export default function AdminCreditsPage() {
  // Pricing rules state
  const [pricingRules, setPricingRules] = useState<PlatformPricingRuleResponse[]>([]);
  const [loadingRules, setLoadingRules] = useState(false);
  const [editingRuleKey, setEditingRuleKey] = useState<string | null>(null);
  const [editingRuleValue, setEditingRuleValue] = useState<number>(1.0);
  const [savingRule, setSavingRule] = useState(false);

  // Orgs credit overview state
  const [orgs, setOrgs] = useState<AdminOrgCreditOverview[]>([]);
  const [totalOrgs, setTotalOrgs] = useState(0);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [offset, setOffset] = useState(0);
  const limit = 20;

  // Manual grant modal state
  const [grantTargetOrg, setGrantTargetOrg] = useState<AdminOrgCreditOverview | null>(null);
  const [grantAmount, setGrantAmount] = useState<number>(25);
  const [grantReason, setGrantReason] = useState("Enterprise Onboarding Grant");
  const [grantNotes, setGrantNotes] = useState("");
  const [submittingGrant, setSubmittingGrant] = useState(false);
  const [grantFeedback, setGrantFeedback] = useState<{ type: "success" | "error"; message: string } | null>(null);

  // Drill-down transaction history modal state
  const [historyTargetOrg, setHistoryTargetOrg] = useState<AdminOrgCreditOverview | null>(null);
  const [orgTransactions, setOrgTransactions] = useState<CreditTransactionRow[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyOffset, setHistoryOffset] = useState(0);
  const historyLimit = 15;

  // 1. Load Platform Pricing Rules
  const fetchPricingRules = useCallback(async () => {
    try {
      setLoadingRules(true);
      const { data } = await api.getPlatformPricingApiV1AdminB2bPricingGet();
      if (data) {
        setPricingRules(data.rules || []);
      }
    } catch (err) {
      console.error("Failed to load platform pricing rules:", err);
    } finally {
      setLoadingRules(false);
    }
  }, []);

  // 2. Load Organization Credits List
  const fetchOrgCredits = useCallback(async () => {
    try {
      setLoadingOrgs(true);
      const { data } = await api.listOrgCreditsApiV1AdminB2bCreditsGet({
        query: {
          search: searchTerm.trim() || undefined,
          limit,
          offset,
        },
      });
      if (data) {
        setOrgs(data.organizations || []);
        setTotalOrgs(data.total || 0);
      }
    } catch (err) {
      console.error("Failed to load organization credit balances:", err);
    } finally {
      setLoadingOrgs(false);
    }
  }, [searchTerm, offset]);

  // 3. Load Org Transaction History
  const fetchOrgHistory = useCallback(async (orgId: number, off = 0) => {
    try {
      setLoadingHistory(true);
      const { data } = await api.getOrgCreditTransactionsApiV1AdminB2bCreditsOrgIdTransactionsGet({
        path: { org_id: orgId },
        query: {
          limit: historyLimit,
          offset: off,
        },
      });
      if (data) {
        setOrgTransactions(data.transactions || []);
        setHistoryTotal(data.total || 0);
      }
    } catch (err) {
      console.error("Failed to load org transaction history:", err);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  useEffect(() => {
    void fetchPricingRules();
  }, [fetchPricingRules]);

  useEffect(() => {
    const timer = setTimeout(() => {
      void fetchOrgCredits();
    }, 200);
    return () => clearTimeout(timer);
  }, [fetchOrgCredits]);

  // Save Pricing Rule
  const handleSavePricingRule = async (ruleKey: string) => {
    setSavingRule(true);
    try {
      const { data, error } = await api.updatePlatformPricingApiV1AdminB2bPricingPut({
        body: {
          rule_key: ruleKey,
          rule_value: Number(editingRuleValue),
        },
      });
      if (error || !data) {
        throw new Error("Failed to update pricing rule");
      }
      setEditingRuleKey(null);
      void fetchPricingRules();
    } catch (err) {
      alert("Failed to save pricing rule. Please try again.");
    } finally {
      setSavingRule(false);
    }
  };

  // Submit Manual Credit Adjustment
  const handleSubmitGrant = async () => {
    if (!grantTargetOrg) return;
    if (grantAmount === 0) {
      setGrantFeedback({ type: "error", message: "Grant amount cannot be zero." });
      return;
    }
    setSubmittingGrant(true);
    setGrantFeedback(null);
    try {
      const { data, error } = await api.grantOrDebitCreditsApiV1AdminB2bCreditsOrgIdGrantPost({
        path: { org_id: grantTargetOrg.organization_id },
        body: {
          amount: Number(grantAmount),
          reason: grantReason.trim() || "Admin manual adjustment",
          notes: grantNotes.trim() || undefined,
        },
      });

      if (error || !data) {
        const errObj = error as any;
        throw new Error(errObj?.error || errObj?.detail || "Failed to process grant adjustment.");
      }

      setGrantFeedback({
        type: "success",
        message: `Successfully adjusted credits for ${grantTargetOrg.organization_name}! New balance: ${data.new_balance} credits.`,
      });

      void fetchOrgCredits();
      setTimeout(() => {
        setGrantTargetOrg(null);
        setGrantFeedback(null);
      }, 1800);
    } catch (err: any) {
      setGrantFeedback({ type: "error", message: err.message || "Adjustment failed." });
    } finally {
      setSubmittingGrant(false);
    }
  };

  const getTxTypeBadge = (type: string) => {
    switch (type.toLowerCase()) {
      case "purchase":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
            <ArrowDownLeft className="w-2.5 h-2.5" /> Purchase
          </span>
        );
      case "settle":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-200">
            <ArrowUpRight className="w-2.5 h-2.5" /> Settle
          </span>
        );
      case "hold":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-200">
            <Clock className="w-2.5 h-2.5" /> Hold
          </span>
        );
      case "refund":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-purple-50 text-purple-700 border border-purple-200">
            <RefreshCw className="w-2.5 h-2.5" /> Refund
          </span>
        );
      case "admin_grant":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-indigo-50 text-indigo-700 border border-indigo-200">
            <Sparkles className="w-2.5 h-2.5" /> Admin Grant
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-700 border border-slate-200">
            {type}
          </span>
        );
    }
  };

  return (
    <div className="space-y-8">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white border border-slate-200 p-6 rounded-3xl shadow-xs">
        <div>
          <h1 className="text-xl font-bold text-slate-900 tracking-tight flex items-center gap-2.5">
            <Coins className="w-6 h-6 text-indigo-600" />
            AI Credits & Platform Pricing Console
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Super Administrator console for configuring dynamic course generation rates and auditing B2B tenant credit ledgers.
          </p>
        </div>
        <button
          onClick={() => {
            void fetchPricingRules();
            void fetchOrgCredits();
          }}
          disabled={loadingRules || loadingOrgs}
          className="px-3.5 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-semibold flex items-center gap-2 transition cursor-pointer self-start sm:self-auto"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loadingRules || loadingOrgs ? "animate-spin" : ""}`} />
          Refresh Console
        </button>
      </div>

      {/* Platform Dynamic Pricing Rules Section */}
      <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-xs space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-sm font-bold text-slate-900 tracking-tight flex items-center gap-2">
              <Sliders className="w-4 h-4 text-indigo-600" />
              Dynamic Platform AI Pricing Rules
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Global consumption rates applied across all B2B course outline generation workflows.
            </p>
          </div>
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 bg-slate-100 px-2.5 py-1 rounded-md">
            Live Global Rules
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
          {pricingRules.map((rule) => {
            const isEditing = editingRuleKey === rule.rule_key;
            return (
              <div
                key={rule.rule_key}
                className="bg-slate-50 border border-slate-200 rounded-2xl p-4 flex flex-col justify-between"
              >
                <div>
                  <span className="text-[10px] font-bold uppercase font-mono text-slate-500">
                    {rule.rule_key}
                  </span>
                  <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                    {rule.description || "Platform AI generation metering parameter"}
                  </p>
                </div>

                <div className="mt-4 pt-3 border-t border-slate-200/80 flex items-center justify-between">
                  {isEditing ? (
                    <div className="flex items-center gap-2 w-full">
                      <input
                        type="number"
                        step="0.1"
                        value={editingRuleValue}
                        onChange={(e) => setEditingRuleValue(parseFloat(e.target.value) || 0)}
                        className="w-24 px-2 py-1 bg-white border border-indigo-500 rounded-lg text-xs font-bold text-slate-900 focus:outline-none"
                      />
                      <button
                        onClick={() => handleSavePricingRule(rule.rule_key)}
                        disabled={savingRule}
                        className="p-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition cursor-pointer"
                        title="Save rule"
                      >
                        <Save className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => setEditingRuleKey(null)}
                        disabled={savingRule}
                        className="p-1.5 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition cursor-pointer"
                        title="Cancel"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="text-xl font-extrabold text-slate-900">
                        {rule.rule_value}{" "}
                        <span className="text-xs font-semibold text-slate-500">
                          {rule.rule_key.includes("max") ? "Lessons" : "Credits"}
                        </span>
                      </div>
                      <button
                        onClick={() => {
                          setEditingRuleKey(rule.rule_key);
                          setEditingRuleValue(rule.rule_value);
                        }}
                        className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-white rounded-lg transition cursor-pointer"
                        title="Edit rate"
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Cross-Organization Credit Ledger */}
      <div className="bg-white border border-slate-200 rounded-3xl shadow-xs overflow-hidden">
        <div className="p-6 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-sm font-bold text-slate-900 tracking-tight flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-indigo-600" />
              Tenant Credit Ledger & Quotas
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Inspect live credit balances, consumption totals, and grant promotional credits.
            </p>
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto">
            <div className="relative w-full sm:w-64">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setOffset(0);
                }}
                placeholder="Search org name or slug..."
                className="w-full pl-9 pr-3.5 py-2 bg-slate-50 border border-slate-200 focus:border-indigo-500 focus:bg-white rounded-xl text-xs font-medium focus:outline-none transition"
              />
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          {loadingOrgs ? (
            <div className="py-16 text-center text-slate-500 flex flex-col items-center justify-center">
              <div className="w-8 h-8 border-3 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mb-3" />
              <p className="text-xs font-medium">Loading organization balances...</p>
            </div>
          ) : orgs.length === 0 ? (
            <div className="py-16 text-center text-slate-400">
              <Coins className="w-10 h-10 mx-auto mb-2 text-slate-300" />
              <p className="text-sm font-semibold text-slate-700">No organizations found</p>
              <p className="text-xs text-slate-400 mt-1">Try refining your search filter.</p>
            </div>
          ) : (
            <table className="w-full text-left border-collapse text-xs">
              <thead>
                <tr className="bg-slate-50/75 border-b border-slate-100 text-slate-500 uppercase tracking-wider font-semibold">
                  <th className="py-3.5 px-6">Organization</th>
                  <th className="py-3.5 px-4">Plan</th>
                  <th className="py-3.5 px-4">Available Balance</th>
                  <th className="py-3.5 px-4">Total Purchased</th>
                  <th className="py-3.5 px-4">Total Consumed</th>
                  <th className="py-3.5 px-4">Recent Activity</th>
                  <th className="py-3.5 px-6 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-slate-700">
                {orgs.map((org) => {
                  const latestTx = org.recent_transactions && org.recent_transactions[0];
                  return (
                    <tr key={org.organization_id} className="hover:bg-slate-50/50 transition">
                      <td className="py-3.5 px-6 whitespace-nowrap">
                        <div className="font-bold text-slate-900">{org.organization_name}</div>
                        <div className="text-[11px] font-mono text-slate-400">
                          {org.organization_slug || `org-${org.organization_id}`}
                        </div>
                      </td>
                      <td className="py-3.5 px-4 whitespace-nowrap">
                        <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase bg-slate-100 text-slate-700 border border-slate-200">
                          {org.plan_tier}
                        </span>
                      </td>
                      <td className="py-3.5 px-4 font-bold whitespace-nowrap">
                        <span
                          className={`text-sm ${
                            (org.ai_credits_balance ?? 0) > 0 ? "text-indigo-600" : "text-slate-500"
                          }`}
                        >
                          {org.ai_credits_balance}
                        </span>{" "}
                        <span className="text-[10px] text-slate-400 font-normal">Credits</span>
                      </td>
                      <td className="py-3.5 px-4 whitespace-nowrap font-medium text-slate-600">
                        {org.ai_credits_total_purchased}
                      </td>
                      <td className="py-3.5 px-4 whitespace-nowrap font-medium text-slate-600">
                        {org.ai_credits_total_consumed}
                      </td>
                      <td className="py-3.5 px-4 whitespace-nowrap">
                        {latestTx ? (
                          <div className="flex items-center gap-1.5">
                            {getTxTypeBadge(latestTx.transaction_type)}
                            <span className="font-mono text-slate-500 text-[11px]">
                              {latestTx.amount > 0 ? `+${latestTx.amount}` : latestTx.amount}
                            </span>
                          </div>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td className="py-3.5 px-6 text-right whitespace-nowrap">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => {
                              setHistoryTargetOrg(org);
                              setHistoryOffset(0);
                              void fetchOrgHistory(org.organization_id, 0);
                            }}
                            className="px-2.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs font-semibold flex items-center gap-1 transition cursor-pointer"
                            title="Inspect transaction history"
                          >
                            <History className="w-3 h-3 text-slate-500" />
                            Ledger
                          </button>
                          <button
                            onClick={() => {
                              setGrantTargetOrg(org);
                              setGrantAmount(25);
                              setGrantReason("Enterprise Onboarding Grant");
                              setGrantNotes("");
                              setGrantFeedback(null);
                            }}
                            className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-bold flex items-center gap-1 transition shadow-2xs cursor-pointer"
                            title="Award or debit credits"
                          >
                            <Sparkles className="w-3 h-3 text-amber-300" />
                            Adjust
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {totalOrgs > limit && (
          <div className="p-4 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
            <div>
              Showing <span className="font-semibold text-slate-800">{offset + 1}</span> to{" "}
              <span className="font-semibold text-slate-800">
                {Math.min(offset + limit, totalOrgs)}
              </span>{" "}
              of <span className="font-semibold text-slate-800">{totalOrgs}</span> organizations
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setOffset((prev) => Math.max(0, prev - limit))}
                disabled={offset === 0}
                className="px-3 py-1.5 border border-slate-200 rounded-lg disabled:opacity-40 hover:bg-slate-50 transition cursor-pointer flex items-center gap-1"
              >
                <ChevronLeft className="w-3.5 h-3.5" /> Previous
              </button>
              <button
                onClick={() => setOffset((prev) => prev + limit)}
                disabled={offset + limit >= totalOrgs}
                className="px-3 py-1.5 border border-slate-200 rounded-lg disabled:opacity-40 hover:bg-slate-50 transition cursor-pointer flex items-center gap-1"
              >
                Next <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Manual Credit Grant / Debit Modal */}
      {grantTargetOrg && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-xs animate-in fade-in duration-150">
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 sm:p-8 shadow-2xl relative overflow-hidden border border-slate-100">
            <button
              onClick={() => setGrantTargetOrg(null)}
              className="absolute top-6 right-6 p-2 text-slate-400 hover:text-slate-700 rounded-xl hover:bg-slate-100 transition cursor-pointer"
              aria-label="Close modal"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-2xl bg-indigo-50 border border-indigo-100 text-indigo-600 flex items-center justify-center shadow-xs">
                <Coins className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900 tracking-tight">
                  Adjust Organization Credits
                </h3>
                <p className="text-xs text-slate-500">
                  Target: <strong className="text-slate-800">{grantTargetOrg.organization_name}</strong> (Current Balance: {grantTargetOrg.ai_credits_balance} Credits)
                </p>
              </div>
            </div>

            {grantFeedback && (
              <div
                className={`my-4 p-3.5 rounded-2xl text-xs font-semibold flex items-center gap-2 ${
                  grantFeedback.type === "success"
                    ? "bg-emerald-50 border border-emerald-200 text-emerald-800"
                    : "bg-rose-50 border border-rose-200 text-rose-800"
                }`}
              >
                {grantFeedback.type === "success" ? (
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                ) : (
                  <AlertCircle className="w-4 h-4 text-rose-600 flex-shrink-0" />
                )}
                <span>{grantFeedback.message}</span>
              </div>
            )}

            <div className="space-y-4 my-6">
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                  Amount (Positive to Grant, Negative to Debit)
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={grantAmount}
                    onChange={(e) => setGrantAmount(parseInt(e.target.value) || 0)}
                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 focus:border-indigo-500 focus:bg-white rounded-xl text-sm font-bold text-slate-900 focus:outline-none transition"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-slate-400">
                    Credits
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                  Grant Reason (Mandatory Audit Log)
                </label>
                <select
                  value={grantReason}
                  onChange={(e) => setGrantReason(e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 focus:border-indigo-500 focus:bg-white rounded-xl text-xs font-medium text-slate-800 focus:outline-none transition"
                >
                  <option value="Enterprise Onboarding Grant">Enterprise Onboarding Grant</option>
                  <option value="Contract Volume Top-up">Contract Volume Top-up</option>
                  <option value="Promotional Trial Credits">Promotional Trial Credits</option>
                  <option value="Billing Dispute Compensation">Billing Dispute Compensation</option>
                  <option value="Manual Debit Adjustment">Manual Debit Adjustment</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                  Additional Notes (Optional)
                </label>
                <textarea
                  rows={3}
                  value={grantNotes}
                  onChange={(e) => setGrantNotes(e.target.value)}
                  placeholder="e.g. Approved per annual enterprise agreement ref #EA-884..."
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 focus:border-indigo-500 focus:bg-white rounded-xl text-xs focus:outline-none transition"
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-4 border-t border-slate-100">
              <button
                onClick={() => setGrantTargetOrg(null)}
                disabled={submittingGrant}
                className="px-4 py-2 border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-bold rounded-xl transition cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitGrant}
                disabled={submittingGrant || grantAmount === 0}
                className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-xs font-bold rounded-xl shadow-xs transition flex items-center gap-2 cursor-pointer"
              >
                {submittingGrant ? (
                  <>
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" /> Recording...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-3.5 h-3.5 text-amber-300" /> Apply Credit Adjustment
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Drill-down Organization Transaction History Modal */}
      {historyTargetOrg && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-xs animate-in fade-in duration-150">
          <div className="bg-white rounded-3xl max-w-4xl w-full p-6 sm:p-8 shadow-2xl relative overflow-hidden border border-slate-100 max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between pb-4 border-b border-slate-100 flex-shrink-0">
              <div>
                <h3 className="text-lg font-bold text-slate-900 tracking-tight flex items-center gap-2">
                  <History className="w-5 h-5 text-indigo-600" />
                  Transaction Ledger: {historyTargetOrg.organization_name}
                </h3>
                <p className="text-xs text-slate-500">
                  Full chronological audit trail for this organization tenant.
                </p>
              </div>
              <button
                onClick={() => setHistoryTargetOrg(null)}
                className="p-2 text-slate-400 hover:text-slate-700 rounded-xl hover:bg-slate-100 transition cursor-pointer"
                aria-label="Close modal"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto py-4">
              {loadingHistory ? (
                <div className="py-16 text-center text-slate-500 flex flex-col items-center justify-center">
                  <div className="w-8 h-8 border-3 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mb-3" />
                  <p className="text-xs font-medium">Loading ledger records...</p>
                </div>
              ) : orgTransactions.length === 0 ? (
                <div className="py-16 text-center text-slate-400">
                  <Coins className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                  <p className="text-sm font-semibold text-slate-700">No transactions recorded</p>
                </div>
              ) : (
                <table className="w-full text-left border-collapse text-xs">
                  <thead>
                    <tr className="bg-slate-50/75 border-b border-slate-100 text-slate-500 uppercase tracking-wider font-semibold">
                      <th className="py-3 px-4">Timestamp</th>
                      <th className="py-3 px-3">Type</th>
                      <th className="py-3 px-3">Amount</th>
                      <th className="py-3 px-3">Status</th>
                      <th className="py-3 px-3">Balance After</th>
                      <th className="py-3 px-4">Notes / Reference</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-slate-700">
                    {orgTransactions.map((tx) => (
                      <tr key={tx.id} className="hover:bg-slate-50/50 transition">
                        <td className="py-3 px-4 font-mono text-slate-500 whitespace-nowrap">
                          {new Date(tx.created_at).toLocaleString()}
                        </td>
                        <td className="py-3 px-3 whitespace-nowrap">
                          {getTxTypeBadge(tx.transaction_type)}
                        </td>
                        <td className="py-3 px-3 font-bold whitespace-nowrap">
                          <span
                            className={
                              tx.amount > 0
                                ? "text-emerald-600"
                                : tx.amount < 0
                                ? "text-rose-600"
                                : "text-slate-600"
                            }
                          >
                            {tx.amount > 0 ? `+${tx.amount}` : tx.amount}
                          </span>
                        </td>
                        <td className="py-3 px-3 whitespace-nowrap font-mono text-[11px]">
                          {tx.status}
                        </td>
                        <td className="py-3 px-3 font-semibold text-slate-900 whitespace-nowrap">
                          {tx.balance_after}
                        </td>
                        <td className="py-3 px-4 text-slate-500 max-w-sm truncate">
                          {tx.notes || (tx.reference_id ? `Ref: ${tx.reference_id}` : "—")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* History Pagination */}
            {historyTotal > historyLimit && (
              <div className="pt-4 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500 flex-shrink-0">
                <div>
                  Showing {historyOffset + 1} to {Math.min(historyOffset + historyLimit, historyTotal)} of {historyTotal} records
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      const newOff = Math.max(0, historyOffset - historyLimit);
                      setHistoryOffset(newOff);
                      void fetchOrgHistory(historyTargetOrg.organization_id, newOff);
                    }}
                    disabled={historyOffset === 0}
                    className="px-3 py-1.5 border border-slate-200 rounded-lg disabled:opacity-40 hover:bg-slate-50 transition cursor-pointer"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => {
                      const newOff = historyOffset + historyLimit;
                      setHistoryOffset(newOff);
                      void fetchOrgHistory(historyTargetOrg.organization_id, newOff);
                    }}
                    disabled={historyOffset + historyLimit >= historyTotal}
                    className="px-3 py-1.5 border border-slate-200 rounded-lg disabled:opacity-40 hover:bg-slate-50 transition cursor-pointer"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
